import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsButton,
  HdsButtonSet,
  HdsModal,
  HdsApplicationState,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  LimitBanner,
  CoreModalConfirm,
  Typography,
} from 'core/components';
import { resourceTypeFor } from 'core/helpers';
import { boxPadding } from 'core/modifiers';
import ListTable from 'hcp/components/service-principal/list-table';

/**
 *
 * `PageServicePrincipalsOrganizationList` lists the organization level HCP Service Principals.
 *
 *
 * ```
 * <Page::ServicePrincipals::Organization::List
 *  @organization={{@organization}}
 *  @policy={{@policy}}
 *  @servicePrincipals={{@servicePrincipals}}
 * />
 * ```
 *
 * @class PageServicePrincipalsOrganizationList
 */
export default class PageServicePrincipalsOrganizationList extends Component {
  /**
   * @argument organization
   * @type {Object}
   */
  /**
   * @argument policy
   * @type {Object}
   */
  /**
   * @argument servicePrincipals
   * @type {Object}
   */
  @service flashMessages;
  @service intl;
  @service router;
  @service api;

  @tracked showDeleteModal = false;
  @tracked returnFocusTo = null;
  @tracked servicePrincipalKeys = null;
  @tracked servicePrincipalToDelete = null;

  confirmDeleteServicePrincipal = task(
    async (servicePrincipal, returnFocusTo) => {
      try {
        let { keys } =
          await this.api.servicePrincipal.servicePrincipalsServiceGetOrganizationServicePrincipal(
            servicePrincipal.organizationId,
            servicePrincipal.id
          );
        this.servicePrincipalKeys = keys;
      } catch {
        this.servicePrincipalKeys = [];
      }
      this.showDeleteModal = true;
      this.servicePrincipalToDelete = servicePrincipal;
      this.returnFocusTo = returnFocusTo;
    }
  );

  deleteServicePrincipal = task(async () => {
    let response;
    let servicePrincipal = this.servicePrincipalToDelete;

    if (servicePrincipal.projectId) {
      response =
        await this.api.servicePrincipal.servicePrincipalsServiceDeleteProjectServicePrincipal(
          servicePrincipal.organizationId,
          servicePrincipal.projectId,
          servicePrincipal.id
        );
    } else {
      response =
        await this.api.servicePrincipal.servicePrincipalsServiceDeleteOrganizationServicePrincipal(
          servicePrincipal.organizationId,
          servicePrincipal.id
        );
    }

    this.onDeleteServicePrincipalSuccess({
      response,
      servicePrincipal,
    });
  });

  onDeleteServicePrincipalSuccess = () => {
    this.resetDeleteServicePrincipal();

    // We should use the router service for this once this PR is merged.
    // via: https://github.com/emberjs/rfcs/issues/592
    let route = getOwner(this).lookup(`route:${this.router.currentRouteName}`);

    this.flashMessages.success(
      this.intl.t(
        'components.page.access-control.service-principals.delete-modal.success'
      )
    );

    return route.refresh();
  };

  resetDeleteServicePrincipal = () => {
    this.showDeleteModal = false;
    this.servicePrincipalToDelete = null;
    this.returnFocusTo = null;
  };

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.IconTile @icon="robot" />
          <PH.Title data-test-service-principals-list-title>
            {{t "components.page.access-control.service-principals.list.title"}}
          </PH.Title>
          <PH.Actions>
            {{#if (can "create service-principal" @servicePrincipals)}}
              <HdsButton
                @color="primary"
                @icon="plus"
                @iconPosition="leading"
                @route="cloud.access-control.service-principals.create"
                @text={{t
                  "components.page.access-control.service-principals.create.action"
                }}
                data-test-service-principals-create
              />
            {{/if}}
          </PH.Actions>
        </HdsPageHeader>
      </:header>
      <:body>
        <section>
          {{#if @servicePrincipals.length}}
            <LimitBanner
              @variant={{resourceTypeFor "service-principal"}}
              @count={{@servicePrincipals.length}}
            />
            <ListTable
              @policy={{@policy}}
              @servicePrincipals={{@servicePrincipals}}
              @servicePrincipalToRoleBindings={{@servicePrincipalToRoleBindings}}
              @confirmDeleteServicePrincipal={{this.confirmDeleteServicePrincipal}}
            />
          {{else}}
            <HdsApplicationState {{boxPadding "2xl 0 2xl 0"}} as |A|>
              <A.Header
                @title={{t
                  "components.page.access-control.service-principals.list.empty.header"
                }}
              />
              <A.Body
                @text={{t
                  "components.page.access-control.service-principals.list.empty.message"
                }}
              />
              {{#if (can "create service-principal" @servicePrincipals)}}
                <A.Footer as |F|>
                  <F.LinkStandalone
                    @icon="plus"
                    @route="cloud.access-control.service-principals.create"
                    @text={{t
                      "components.page.access-control.service-principals.list.empty.action"
                    }}
                    data-test-service-principals-empty-create
                  />
                </A.Footer>
              {{/if}}
            </HdsApplicationState>
          {{/if}}
        </section>

        {{#if this.showDeleteModal}}
          {{#if this.servicePrincipalKeys.length}}
            <HdsModal
              @color="critical"
              @onClose={{this.resetDeleteServicePrincipal}}
              id="form-modal"
              data-test-cant-delete-modal
              as |M|
            >
              <M.Header
                @icon="alert-triangle"
                data-test-cant-delete-modal-title
              >
                {{t
                  "components.page.access-control.service-principals.delete-modal.not-allowed-title"
                }}
              </M.Header>
              <M.Body>
                <Typography
                  @variant="body1"
                  class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                  data-test-cant-delete-modal-message
                >
                  {{t
                    "components.page.access-control.service-principals.delete-modal.errorSPKeysPresent"
                    servicePrincipalName=this.servicePrincipalToDelete.name
                    servicePrincipalKeysCount=this.servicePrincipalKeys.length
                    htmlSafe=true
                  }}
                </Typography>
              </M.Body>
              <M.Footer as |F|>
                <HdsButtonSet>
                  <HdsButton
                    @color="secondary"
                    @text={{t "components.form.close"}}
                    data-test-service-principal-modal-close
                    {{on "click" F.close}}
                  />
                </HdsButtonSet>
              </M.Footer>
            </HdsModal>
          {{else}}
            <CoreModalConfirm
              @confirmText={{t "components.core.modal.confirm.delete"}}
              @headerIcon="alert-triangle"
              @headerText={{t
                "components.page.access-control.service-principals.delete-modal.title"
              }}
              @onCancel={{this.resetDeleteServicePrincipal}}
              @onSubmit={{this.deleteServicePrincipal}}
              data-test-delete-service-principal-modal
            />
          {{/if}}
        {{/if}}
      </:body>
    </CoreLayoutMain>
  </template>
}
